import { isBookingPath } from 'shared/business-websites/routing';
import { websiteAppearanceBootstrap } from 'shared/business-websites/theme/appearance';
import '@fontsource-variable/onest/index.css';
import { DateTime, Settings as LuxonSettings } from 'luxon';
import React from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import axios from 'shared/anaxios';
import Button from 'shared/button';
import { ViewErrorBoundary } from 'shared/error-boundry';
import { Box, Flex, Grid } from 'shared/grid';
import Loader from 'shared/loader';
import 'shared/loader.css';
import { StandardModal } from 'shared/modal';
import { fetchBookableBusy } from 'shared/redux/bookable-busy/actions';
import { fetchIPLocation } from 'shared/redux/geoip/actions';
import { fetchSchedules } from 'shared/redux/schedules/actions';
import { updatePreferences } from 'shared/redux/user-preferences/actions';
import Toaster from 'shared/toaster';
import { addToast } from 'shared/toaster/redux/actions';
import sharedFrench from 'shared/translations/fr.json';
import { CroppedText, Link } from 'shared/typography';
import { getErrorInterceptor } from 'shared/utils/http-error-handling';
import { getBrowserLocale } from 'shared/utils/locale';
import { getBrowserTimezone } from 'shared/utils/time';
import { hasNewVersion, safeReloadForNewVersion } from 'shared/utils/version';

import { WebsiteIcons } from './website-icons';
import { useAppDispatch, useAppSelector } from './store';
import french from './translations/fr.json';
import afrikaans from './translations/af.json';
import sharedAfrikaans from 'shared/translations/af.json';

import { Global, ThemeProvider } from '@emotion/react';

import { config } from '@fortawesome/fontawesome-svg-core';
// Keep this asset external: the SSR server inlines ordinary CSS imports.
import fontAwesomeStylesUrl from '@fortawesome/fontawesome-svg-core/styles.css?url';
import { Provider as ReduxProvider } from 'react-redux';
import ErrorBoundary from 'shared/error-boundry';
import globalStyle from 'shared/global-style';
import { ModalToastProvider } from 'shared/modal/context';
import initWhatInput from 'shared/what-input-with-shadow-root';
// import 'what-input';
import { faChevronLeft, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIsRecaptchaReady } from 'shared/hooks/use-recaptcha';
import NotFoundImage from 'shared/images/404-1.gif';
import settings from 'shared/settings';
import { getCdnImageUrlFromId } from 'shared/utils/images';
import theme from './theme';

config.autoAddCss = false;

const messages = {
  fr: { ...sharedFrench, ...french },
  af: { ...sharedAfrikaans, ...afrikaans },
  en: {},
};

const isServer = typeof window === 'undefined';

if (!isServer && __TARGET_ENV__ !== 'dev') {
  hasNewVersion().then((hasNew) => hasNew && safeReloadForNewVersion());
}

const confLuxon = () => {
  LuxonSettings.defaultZone = getBrowserTimezone();
  LuxonSettings.throwOnInvalid = true;
};
// prevent it from taking the site down
setTimeout(confLuxon);

const App = ({
  children,
  helmetContext,
  isFaqPage = false,
}: {
  children: React.ReactNode;
  helmetContext?: any;
  isFaqPage?: boolean;
}) => {
  React.useEffect(() => {
    initWhatInput();
  }, []);

  const [isUpdateModalOpen, setUpdateModalOpen] = React.useState(false);
  const updateIntervalIdRef = React.useRef(null);
  const sessionIntervalIdRef = React.useRef(null);
  const businessId = useAppSelector((state) => state.public.businessId);
  const business = useAppSelector((state) => state.businesses.data[businessId]);
  const language = useAppSelector((state) => state.userPreferences.language);
  const businessLanguage = business?.language || 'en';
  const hasWebsite = useAppSelector((state) => !!state.website?.website);

  const notFound = useAppSelector(
    (state) => state.businesses.status === 'failed',
  );

  const isLoading = useAppSelector((state) =>
    (hasWebsite || isFaqPage
      ? [state.businesses.status]
      : [
          state.businesses.status,
          state.locations.status,
          state.businesses.status,
          state.services.status,
          state.bookables.status,
          state.bookableGroups.status,
        ]
    )
      .map((v) => v || 'loading')
      .includes('loading'),
  );

  const dispatch = useAppDispatch();

  const userLocale = getBrowserLocale();

  const localeCandidate =
    language.substring(0, 2) === userLocale.substring(0, 2)
      ? userLocale
      : language;

  const locale = Intl.NumberFormat.supportedLocalesOf([
    localeCandidate,
    'en-GB',
  ])[0];

  React.useEffect(() => {
    LuxonSettings.defaultLocale = locale;
  }, [language, locale]);

  const setLanguage = React.useCallback(
    (language) => {
      dispatch(updatePreferences({ language }));
    },
    [dispatch],
  );

  React.useEffect(() => {
    if (!hasWebsite) setLanguage(businessLanguage);
  }, [businessLanguage, setLanguage, hasWebsite]);

  React.useEffect(() => {
    // const handle = window.location.host.split('.')[0];

    const updateAvailability = () => {
      if (
        !business ||
        (!isBookingPath(window.location.pathname) &&
          (hasWebsite || window.location.pathname !== '/'))
      ) {
        return;
      }

      const [start, end] = business.settings.booking_window.map(
        (v) =>
          DateTime.utc().plus({ minutes: v }).toISO().substring(0, 19) + 'Z',
      );

      dispatch(fetchSchedules(business.id));
      dispatch(fetchBookableBusy(business.id, start, end));
    };

    // updateAvailability();
    updateIntervalIdRef.current = setInterval(
      updateAvailability,
      5 * 60 * 1000,
    );

    // dispatch(fetchLocations(business.id));
    // dispatch(fetchServices(business.id, true));
    // dispatch(fetchBookableGroups(business.id));
    // dispatch(fetchBookables(business.id));
    // dispatch(fetchBillableItems({ businessId: business.id }));
    // dispatch(fetchInvoiceSettings(business.id));
    dispatch(fetchIPLocation());

    sessionIntervalIdRef.current = setInterval(() => {
      hasNewVersion().then((hasNew) => hasNew && setUpdateModalOpen(true));
    }, 60000);

    return () => {
      clearInterval(updateIntervalIdRef.current);
      clearInterval(sessionIntervalIdRef.current);
    };
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const [wasLoaded, setWasLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!isLoading) {
      setWasLoaded(true);
    }
  }, [isLoading]);

  const isRecaptchaReady = useIsRecaptchaReady();

  if (notFound) {
    return (
      <>
        <title>Not found</title>

        <Flex
          justifyContent="center"
          alignItems="center"
          flex={1}
          height="100vh"
        >
          <Grid textAlign="center">
            <CroppedText as="h1" fontSize={6} color="text">
              <FormattedMessage
                defaultMessage="Business not found"
                description="Heading shown when the business was not found"
              />
            </CroppedText>
            <Link
              variant="discreetLink"
              href={`//${settings.bookemBaseDomain}`}
            >
              <FontAwesomeIcon icon={faChevronLeft} />{' '}
              <FormattedMessage
                defaultMessage="Take me back"
                description="Link text to go back to main site when business not found"
              />
            </Link>
          </Grid>

          <Box
            as="img"
            sx={{
              position: 'fixed',
              bottom: 0,
              right: 0,
              height: '40%',
            }}
            src={NotFoundImage}
            alt="Not found"
            title="Not found"
          />
        </Flex>
      </>
    );
  }

  if (!businessId) {
    return (
      <>
        <title>Error</title>
        <Flex
          justifyContent="center"
          alignItems="center"
          flex={1}
          height="100vh"
        >
          <Grid textAlign="center">
            <CroppedText as="h1" fontSize={6} color="text">
              <FormattedMessage
                defaultMessage="Something went wrong"
                description="Generic error heading when something went wrong"
              />
            </CroppedText>
            <Link
              variant="discreetLink"
              as="button"
              type="button"
              onClick={() => window.location.reload()}
            >
              <FontAwesomeIcon icon={faRefresh} />{' '}
              <FormattedMessage
                defaultMessage="Reload"
                description="Button text to reload the page on error"
              />
            </Link>
          </Grid>
        </Flex>
      </>
    );
  }

  if (isLoading && !wasLoaded) {
    return (
      <>
        {!hasWebsite && <title>{business?.name}</title>}
        <Loader />
      </>
    );
  }

  return (
    <Flex bg="background" flexDirection="column" minHeight="100vh">
      {!hasWebsite && <title>{business?.name}</title>}

      <WebsiteIcons />

      {!hasWebsite && (
        <meta name="description" content={business?.description} />
      )}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />

      {!hasWebsite && <meta property="og:title" content={business?.name} />}
      {!hasWebsite && (
        <meta property="og:description" content={business?.description} />
      )}
      {!hasWebsite && <meta property="og:site_name" content={business?.name} />}
      {!hasWebsite && <meta property="og:type" content="website" />}

      {!hasWebsite && (business.logo_image_id || business.cover_image_id) && (
        <meta
          property="og:image"
          content={
            (business.logo_image_id
              ? getCdnImageUrlFromId(business.logo_image_id)
              : null) ||
            (business.cover_image_id
              ? getCdnImageUrlFromId(business.cover_image_id)
              : null)
          }
        />
      )}

      <Toaster />

      {/* <React.Suspense fallback={<Loader className="suspense" />}> */}
      <ViewErrorBoundary>{children}</ViewErrorBoundary>
      {/* </React.Suspense> */}

      <StandardModal
        isOpen={isUpdateModalOpen}
        close={() => setUpdateModalOpen(false)}
        title={
          <FormattedMessage
            defaultMessage="This page has been updated"
            description="Title of modal shown when a new version of the page is available"
          />
        }
      >
        {() => (
          <Grid>
            <CroppedText p={4} textAlign="center">
              <FormattedMessage
                defaultMessage="Please reload the page, unless you are in the middle of something."
                description="Message asking the user to reload because a new version is available"
              />
            </CroppedText>

            <Flex justifyContent="space-between" flexWrap="wrap">
              <Button
                width={['100%', 'auto']}
                onClick={() => safeReloadForNewVersion()}
              >
                <FormattedMessage
                  defaultMessage="Reload"
                  description="Primary button text to reload the page for updates"
                />
              </Button>
              <Button
                mt={[4, 0]}
                width={['100%', 'auto']}
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setUpdateModalOpen(false);
                  setTimeout(
                    () => {
                      setUpdateModalOpen(true);
                    },
                    5 * 60 * 1000,
                  );
                }}
              >
                <FormattedMessage
                  defaultMessage="Remind me in 5 minutes"
                  description="Secondary button text to snooze the update reminder for 5 minutes"
                />
              </Button>
            </Flex>
          </Grid>
        )}
      </StandardModal>
    </Flex>
  );
};

export const AppWithIntl = ({
  children,
  isFaqPage = false,
}: {
  children: React.ReactNode;
  isFaqPage?: boolean;
}) => {
  const language = useAppSelector((state) => state.userPreferences.language);
  const userLocale = getBrowserLocale();
  const localeCandidate =
    language.substring(0, 2) === userLocale.substring(0, 2)
      ? userLocale
      : language;
  const locale = Intl.NumberFormat.supportedLocalesOf([
    localeCandidate,
    'en-GB',
  ])[0];

  return (
    <IntlProvider
      key={language}
      locale={locale}
      defaultLocale="en-GB"
      messages={messages[language.substring(0, 2)]}
    >
      <App isFaqPage={isFaqPage}>{children}</App>
    </IntlProvider>
  );
};

const WrappedApp = ({
  children,
  store,
  persistor,
  css = '',
  isDev = false,
  isFaqPage = false,
}) => {
  const websiteData = store.getState().website;
  const appearanceScript = websiteData?.website
    ? websiteAppearanceBootstrap(
        websiteData.website.settings,
        websiteData.business_id,
        !!websiteData.preview_token,
      )
    : '';
  React.useEffect(() => {
    axios.interceptors.response.use(
      (response) => response,
      getErrorInterceptor(
        () => null,
        (message) =>
          store.dispatch(
            addToast(
              message[0],
              message[1] as Parameters<typeof addToast>[1],
              message[2],
            ),
          ),
      ),
    );
  });
  return (
    <html
      lang={store.getState().userPreferences.language || 'en'}
      suppressHydrationWarning={!!appearanceScript}
    >
      <head>
        <link rel="stylesheet" href={fontAwesomeStylesUrl} />
        <meta charSet="utf-8" />
        {appearanceScript && (
          <script dangerouslySetInnerHTML={{ __html: appearanceScript }} />
        )}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, interactive-widget=resizes-content"
        />
        {isDev && (
          <>
            <script type="module" src="/@vite/client"></script>
            <script
              type="module"
              dangerouslySetInnerHTML={{
                __html: `
                  import RefreshRuntime from '/@react-refresh';
                  RefreshRuntime.injectIntoGlobalHook(window);
                  window.$RefreshReg$ = () => {};
                  window.$RefreshSig$ = () => (type) => type;
                  window.__vite_plugin_react_preamble_installed__ = true;
                `,
              }}
            />
          </>
        )}
        {css && <style suppressHydrationWarning>{css}</style>}
      </head>
      <body>
        {/* <CacheProvider value={myCache}> */}
        <ThemeProvider theme={theme}>
          <Global styles={globalStyle as any} />
          <ReduxProvider store={store}>
            <ModalToastProvider>
              <ErrorBoundary>
                <AppWithIntl isFaqPage={isFaqPage}>{children}</AppWithIntl>
              </ErrorBoundary>
            </ModalToastProvider>
          </ReduxProvider>
        </ThemeProvider>
        {/* </CacheProvider> */}
      </body>
    </html>
  );
};

export default WrappedApp;
